/**
 * 触屏机的接口清单。
 *
 * 全部复用后端**已有的顾客端接口**，唯一新增的是支付那几个（出码/轮询/模拟确认）。
 * 这就是「一套后端、两个入口」的落地方式：触屏机不给后端加专属逻辑，
 * 手机扫码端能用什么，触屏机就用什么。
 */
import type { ApiClient } from './client';
import type {
  CreatedOrder,
  CustomerOrderDetail,
  MenuResponse,
  TableMenuResponse,
  PayIntent,
  PayStatus,
  PaymentConfig,
  PreviewResult,
} from './types';

/** 下单/试算里的一项 */
export interface OrderItemPayload {
  productId: string;
  variantIds?: string[];
  quantity: number;
  remark?: string;
  /** 前端当时展示的单价，服务端据此检测「菜单在我们展示之后被改过价」 */
  expectedUnitPriceCents?: number;
}

/**
 * 下单时的「我是谁」。
 *
 * 二选一（后端也是这么建模的）：
 *   · storeCode + customerOpenId —— 店里那台一体机（设备号认顾客，没有桌号）
 *   · qrToken                    —— 顾客扫桌上的二维码（令牌里就带着桌号）
 */
export interface SubmitOrderPayload {
  storeCode?: string;
  qrToken?: string;
  channel: 'KIOSK' | 'MINI_PROGRAM';
  customerOpenId?: string;
  /** 幂等键：同一顾客重复点提交只会产生一张单 */
  idempotencyKey: string;
  remark?: string;
  items: OrderItemPayload[];
}

export interface Credential {
  openId?: string;
  qrToken?: string;
}

export function createEndpoints(client: ApiClient) {
  const credentialQuery = (credential: Credential): string => {
    if (credential.openId) return `openId=${encodeURIComponent(credential.openId)}`;
    if (credential.qrToken) return `t=${encodeURIComponent(credential.qrToken)}`;
    return '';
  };

  return {
    /** 取菜单 */
    getMenu(storeCode: string) {
      return client.request<MenuResponse>(`/menu?storeCode=${encodeURIComponent(storeCode)}`);
    },

    /** 扫桌台令牌取菜单（table 模式）。返回里带门店和桌号，页面据此显示「你在哪一桌」 */
    getTableMenu(qrToken: string) {
      return client.request<TableMenuResponse>(`/scan/${encodeURIComponent(qrToken)}`);
    },

    /** 试算：顾客点「去结算」时调一次，金额以它为准 */
    previewOrder(scope: { storeCode?: string; qrToken?: string }, items: OrderItemPayload[]) {
      return client.request<PreviewResult>('/orders/preview', {
        method: 'POST',
        body: { ...scope, items },
      });
    },

    /** 下单（金额由服务端重算，前端传不了金额） */
    submitOrder(payload: SubmitOrderPayload) {
      return client.request<CreatedOrder>('/orders', { method: 'POST', body: payload });
    },

    /** 发起扫码支付：拿二维码内容 */
    createPayIntent(orderNo: string, credential: Credential) {
      return client.request<PayIntent>(`/orders/${encodeURIComponent(orderNo)}/pay-intent`, {
        method: 'POST',
        body: credential,
      });
    },

    /** 轮询支付状态（超时给短一点，它是每 2 秒一次的请求） */
    getPayStatus(orderNo: string, credential: Credential) {
      const query = credentialQuery(credential);
      return client.request<PayStatus>(
        `/orders/${encodeURIComponent(orderNo)}/pay-status${query ? `?${query}` : ''}`,
        { timeoutMs: 6_000 },
      );
    },

    /**
     * 模拟通道「确认收款」。
     * ⚠️ 只在后端处于模拟模式时可用；真实模式下会返回 501，
     *    前端也只在 allowTestPay 打开时才显示入口。
     */
    confirmTestPayment(orderNo: string, credential: Credential) {
      return client.request<{ duplicated: boolean }>(
        `/orders/${encodeURIComponent(orderNo)}/payments/test-confirm`,
        { method: 'POST', body: credential },
      );
    },

    /**
     * 顾客查自己的订单（电子小票页用它）。
     * 必须带凭据（openId 或桌台令牌），否则后端返回 404 —— 这也是
     * 「小票链接被别人捡到也看不到别人的单」的依据。
     */
    getOrderForCustomer(orderNo: string, credential: Credential) {
      const query = credentialQuery(credential);
      return client.request<CustomerOrderDetail>(
        `/orders/${encodeURIComponent(orderNo)}${query ? `?${query}` : ''}`,
        { timeoutMs: 8_000 },
      );
    },

    /** 后端当前的支付能力（真实 / 模拟） */
    getPaymentConfig() {
      return client.request<PaymentConfig>('/payments/config', { timeoutMs: 6_000 });
    },
  };
}

export type Endpoints = ReturnType<typeof createEndpoints>;
